import { useTranslation } from 'next-i18next/pages';
import { EnergyClassLabelMap, getCountryColorMap } from '@pkgs/components/consts/countryData';
import { EnergyEfficiency } from '@pkgs/components';
import { CountryCode, type EnergyClass } from '@pkgs/api';
import { Tooltip } from '@ev-tech/ui-core-components/Tooltip';
import { Copy } from '@ev-tech/ui-core-components/Copy';

import { TRANSLATION_NAMESPACE } from '@/i18n/config';

import styles from './EnergyEfficiencyPopup.module.css';

type EnergyEfficiencyPopupProps = {
  energyClass: EnergyClass;
  listingCountryCode: CountryCode;
};

// Product decision: Belgium and Ireland use their own scales. Every other listing country keeps the German scale.
const getEnergyScaleCountryCode = (listingCountryCode: CountryCode): CountryCode => {
  if (listingCountryCode === CountryCode.BE || listingCountryCode === CountryCode.IE) return listingCountryCode;

  return CountryCode.DE;
};

export const EnergyEfficiencyPopup = ({ energyClass, listingCountryCode }: EnergyEfficiencyPopupProps) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const humanReadableEnergyClass = EnergyClassLabelMap[energyClass];
  const scaleCountryCode = getEnergyScaleCountryCode(listingCountryCode);
  const scaleColorMap = getCountryColorMap(scaleCountryCode);
  const shouldDisplayEfficiencyScalePopup = scaleColorMap.some(([colorMapEnergyClass]) => colorMapEnergyClass === energyClass);

  if (!shouldDisplayEfficiencyScalePopup) return humanReadableEnergyClass;

  return (
    <div className={styles.container}>
      <EnergyEfficiency
        className={styles.energyEfficiencyButton}
        energyClass={energyClass}
        countryCode={scaleCountryCode}
        variant="basic"
        data-testid="energy-efficiency-popup-button"
      />
      <Tooltip side="bottom" variant="light" label={t('search.expose.propertyDetails.energy.label.energyEfficiencyClass')} closeLabel="">
        <div className={styles.contentContainer}>
          <Copy text={t('search.expose.propertyDetails.energy.label.energyEfficiencyClass')} size="m" bold />
          <Copy text={t('search.expose.propertyDetails.energy.energyEfficiencyClass.description')} size="m" />
          <EnergyEfficiency className={styles.energyEfficiency} energyClass={energyClass} countryCode={scaleCountryCode} />
        </div>
      </Tooltip>
    </div>
  );
};
